import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject, map } from 'rxjs';

import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { default as intlTelInput } from 'intl-tel-input';
import { UserService } from 'app/services/utilities/user.service';
import { HelperStoreService } from 'app/services/store/helper-store.service';
declare var $: any;

@Component({
    selector: 'app-phone',
    templateUrl: './phone.component.html',
    styleUrls: ['./phone.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        { provide: MatFormFieldControl, useExisting: PhoneComponent }
    ]
})
export class PhoneComponent implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string> {
    static nextId = 0;
    @Input() autoComplete: boolean = false;
    @Input() appearance: string = 'standard';
    stateChanges = new Subject<void>();
    focused: boolean = false;
    controlType: string = 'phone-input';
    autofilled?: boolean;
    validateFn: ValidatorFn;

    @HostBinding() id = `phone-input-${PhoneComponent.nextId++}`;
    @HostBinding('attr.aria-describedby') describedBy = '';


    private _placeholder: string;
    private intlTelinputInstance: any;
    private _required = false;
    private _readonly = false;
    private _disabled = false;
    private _value = null;

    constructor(
        @Optional() @Self() public ngControl: NgControl,
        public userService: UserService,
        private fm: FocusMonitor,
        private elRef: ElementRef<HTMLElement>,
        private helperStoreService: HelperStoreService
    ) {
        fm.monitor(elRef, true).subscribe(origin => {
            if (this.focused && !origin) {
                this.onTouched();
            }
            this.focused = !!origin;
            this.stateChanges.next();
        });
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    @HostBinding('class.floating') get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    @Input()
    get placeholder() { return this._placeholder; }
    set placeholder(plh) {
        this._placeholder = plh;
        this.stateChanges.next();
    }

    @Input()
    get required() { return this._required; }
    set required(req) {
        this._required = coerceBooleanProperty(req);
        this.stateChanges.next();
    }

    @Input()
    get readonly() { return this._readonly; }
    set readonly(read) {
        this._readonly = coerceBooleanProperty(read);
        this.stateChanges.next();
    }

    @Input()
    get disabled(): boolean { return this._disabled; }
    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    @Input() get value(): string | null { return this._value; }
    set value(val: string | null) {
        this._initIntlTelInput();
        if (val) {
            this.intlTelinputInstance.setNumber(val);
        }
        this._value = this.intlTelinputInstance.getNumber();
        this.onChange(this._value);
        this.stateChanges.next();
    }

    get input(): HTMLInputElement {
        return this.elRef.nativeElement.querySelector('input');
    }
    get empty() {
        return !this.value;
    }
    get errorState() {
        return this.ngControl.errors !== null && !!this.ngControl.touched;
    }

    get userPhoneNumberAutocomplete$() {
        return this.helperStoreService.getHelpersAndAdminsWithPhonenumber().pipe(
            map(h => h.filter(user => !user.ivrExtension))
        )
    }

    ngOnInit() {
        this._initIntlTelInput();
        this.validateFn = this.createPhoneNumberValidator();
        if (this.ngControl && this.ngControl.control) {
            this.ngControl.control.setValidators(Validators.compose([this.ngControl.control.validator, this.validateFn]));
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
        this.fm.stopMonitoring(this.elRef);
    }

    setValue(event: Event) {
        this.value = (<HTMLInputElement>event.target).value;
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join(' ');
    }
    onContainerClick(event: MouseEvent): void {
        if ((event.target as Element).tagName.toLowerCase() !== 'input') {
            this.input.focus();
        }
    }

    onChange = (_: any) => { };
    onTouched = () => { };

    writeValue(value: string): void {
        if (value) {
            this.value = value;
        }
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private _initIntlTelInput() {
        if (!this.intlTelinputInstance) {
            this.intlTelinputInstance = intlTelInput(this.input, {
                initialCountry: 'auto',
                preferredCountries: ['fr', 'us', 'gb'],
                geoIpLookup: async (callback) => {
                    let country = 'fr';
                    await fetch('https://api.ip2loc.com/8GPZ8UXNsBeXNCTC9WOayJexEdM9UiD5/detect')
                        .then(data => data.json())
                        .then(data => {
                            if (data.success) {
                                country = data.location.country.alpha_2.toLowerCase();
                            }
                        });
                    callback(country);
                },
            });
        }
    }

    private createPhoneNumberValidator() {
        return (_control: UntypedFormControl): ValidationErrors => {
            if (!this.required && !this.value) {
                return null;
            }
            if (this.intlTelinputInstance.defaultCountry === 'se' && _control?.value?.length === 16) {
                return null;
            }
            const isNotValid = this.intlTelinputInstance.isValidNumber() === false;
            return isNotValid && { invalid: true } || null;
        };
    }
}
